import { IonContent, useIonRouter } from '@ionic/react';
import { Button } from '@up2stay/up2promo-ui';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@up2stay/up2promo-ui';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { Component as ReactComponent } from 'react';
import { shellPaths } from '@/core/router/app-paths';

import { captureException } from '@/core/posthog-init';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { AppIonPage } from '@/core/components/app-ion-page';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ComponentType<ErrorFallbackProps>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorFallbackProps {
  error: Error | null;
  resetError: () => void;
}

/**
 * Error fallback page that displays when an error occurs.
 * This component is used within ErrorBoundary, which is placed inside
 * DictionaryWrapper and IonReactRouter, so both dictionary and router are available.
 */
function ErrorFallbackPage({ error, resetError }: ErrorFallbackProps) {
  const router = useIonRouter();
  const dict = useDictionary();
  const errorDict = dict.error;

  return (
    <AppIonPage>
      <IonContent>
        <div className="flex h-full items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-10 w-10 text-destructive" />
              </div>
              <CardTitle className="text-2xl">{errorDict.title}</CardTitle>
              <CardDescription>{errorDict.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && import.meta.env.DEV && (
                <div className="rounded-md bg-muted p-3">
                  <p className="text-xs font-mono text-muted-foreground">
                    {error.message}
                  </p>
                  {error.stack && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-muted-foreground">
                        Stack trace
                      </summary>
                      <pre className="mt-2 max-h-40 overflow-auto text-xs text-muted-foreground">
                        {error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  onClick={resetError}
                  size="lg"
                  variant="outline"
                  className="w-full"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {errorDict.tryAgain}
                </Button>
                <Button
                  onClick={() =>
                    router.push(shellPaths.home, 'root', 'replace')
                  }
                  size="lg"
                  className="w-full"
                >
                  <Home className="mr-2 h-4 w-4" />
                  {errorDict.goToDashboard}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </IonContent>
    </AppIonPage>
  );
}

export class ErrorBoundary extends ReactComponent<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    captureException(error, {
      errorBoundary: true,
      componentStack: errorInfo.componentStack ?? undefined,
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || ErrorFallbackPage;
      return (
        <FallbackComponent
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}
